import clsx from 'clsx';
import React from 'react';

/**
 * Full-width strip of headline figures above a list: its own card, between
 * the page title and the list card (`ListPage`'s `summary`), split into
 * equal cells by hairlines. Cells share a row from `sm`; on phones they sit
 * two to a row, and a lone last cell spans the row.
 */
export const SummaryFacts: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    className={clsx(
      'grid w-full grid-cols-2 gap-px overflow-hidden rounded-v2-xl border border-v2-line bg-v2-line shadow-v2-card sm:auto-cols-fr sm:grid-flow-col sm:grid-cols-none',
      className,
    )}
  >
    {children}
  </div>
);

/** One cell of `SummaryFacts`: label, figure, then an optional caption. */
export const SummaryFact: React.FC<{
  value: React.ReactNode;
  label: string;
  /** Quiet line under the figure ("this month"). */
  secondary?: React.ReactNode;
  className?: string;
}> = ({ value, label, secondary, className }) => (
  <div
    role="group"
    aria-label={label}
    className={clsx(
      'flex min-w-0 flex-col bg-v2-surface px-4 pb-[13px] pt-3.5 odd:last:col-span-2 sm:px-5 sm:odd:last:col-span-1',
      className,
    )}
  >
    <span className="truncate text-[10.5px] font-semibold uppercase leading-[1.5] tracking-[0.1em] text-v2-fg-3">
      {label}
    </span>
    <span className="v2-num mt-[3px] text-[23px] font-bold leading-[1.15] tracking-[-0.03em] text-v2-fg">
      {value}
    </span>
    {secondary && (
      <span className="v2-num mt-0.5 truncate text-[11.5px] text-v2-fg-3">
        {secondary}
      </span>
    )}
  </div>
);
